import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/api';
import { toastSuccess, toastError } from '../utils/toasts';

const SmsCampaign = () => {
  const [clients, setClients] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [content, setContent] = useState('');
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [tab, setTab] = useState('send');
  const [channel, setChannel] = useState('whatsapp');
  const [clientSearch, setClientSearch] = useState('');
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [filterClientId, setFilterClientId] = useState('');

  const fetchClients = useCallback(async () => {
    try {
      const res = await api.get('/clients', {
        params: { limit: 500, status: 'actif' },
      });
      const list = res.data?.clients || res.data?.data || res.data || [];
      setClients(list.filter((c) => c.phone));
    } catch {
      toastError('Erreur chargement clients');
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const params = { page: historyPage, limit: 20 };
      if (filterClientId) params.clientId = filterClientId;
      const res = await api.get('/sms/history', { params });
      setHistory(res.data.logs || []);
      setHistoryTotal(res.data.total || 0);
    } catch {
      toastError('Erreur chargement historique');
    }
  }, [historyPage, filterClientId]);

  useEffect(() => { fetchClients(); }, [fetchClients]);
  useEffect(() => { if (tab === 'history') fetchHistory(); }, [tab, fetchHistory]);

  const filteredClients = clients.filter((c) =>
    c.companyName?.toLowerCase().includes(clientSearch.toLowerCase()) ||
    c.phone?.includes(clientSearch) ||
    c.contactName?.toLowerCase().includes(clientSearch.toLowerCase())
  );

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredClients.map((c) => c._id));
    }
    setSelectAll(!selectAll);
  };

  const handleSend = async () => {
    if (!content.trim()) { toastError('Le message est vide'); return; }
    if (!selectedIds.length) { toastError('Sélectionnez au moins un client'); return; }

    setSending(true);
    try {
      const endpoint = channel === 'whatsapp' ? '/sms/whatsapp/broadcast' : '/sms/broadcast';
      const label = channel === 'whatsapp' ? 'WhatsApp' : 'SMS';
      const res = await api.post(endpoint, {
        clientIds: selectedIds,
        content: content.trim(),
      });
      toastSuccess(res.data.message || `${label} envoyés`);
      setContent('');
      setSelectedIds([]);
      setSelectAll(false);
    } catch (err) {
      toastError(err.response?.data?.error || `Erreur envoi ${channel}`);
    } finally {
      setSending(false);
    }
  };

  const handleSendSingle = async (client) => {
    if (!content.trim()) { toastError('Le message est vide'); return; }
    setSending(true);
    try {
      const endpoint = channel === 'whatsapp' ? '/sms/whatsapp/send' : '/sms/send';
      const label = channel === 'whatsapp' ? 'WhatsApp' : 'SMS';
      await api.post(endpoint, {
        clientId: client._id,
        content: content.trim(),
      });
      toastSuccess(`${label} envoyé à ${client.companyName}`);
    } catch (err) {
      toastError(err.response?.data?.error || `Erreur envoi ${channel}`);
    } finally {
      setSending(false);
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'sent') return <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">Envoyé</span>;
    return <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-medium">Échec</span>;
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-800">Campagne WhatsApp / SMS</h1>

      {/* Tabs */}
      <div className="flex gap-4 border-b border-gray-200">
        <button
          onClick={() => setTab('send')}
          className={`pb-3 text-sm font-medium transition-colors ${
            tab === 'send'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Envoyer un message
        </button>
        <button
          onClick={() => setTab('history')}
          className={`pb-3 text-sm font-medium transition-colors ${
            tab === 'history'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Historique
        </button>
      </div>

      {tab === 'send' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Liste clients */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-4 border-b border-gray-100">
              <input
                type="text"
                placeholder="Rechercher un client par nom, téléphone..."
                value={clientSearch}
                onChange={(e) => setClientSearch(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="p-2 border-b border-gray-100 flex items-center gap-2">
              <input
                type="checkbox"
                checked={selectAll && filteredClients.length > 0}
                onChange={handleSelectAll}
                className="rounded"
              />
              <span className="text-sm text-gray-600">
                {selectAll ? 'Tout désélectionner' : 'Tout sélectionner'} ({filteredClients.length} clients avec téléphone)
              </span>
            </div>
            <div className="max-h-96 overflow-y-auto divide-y divide-gray-100">
              {filteredClients.map((client) => (
                <label
                  key={client._id}
                  className={`flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors ${
                    selectedIds.includes(client._id) ? 'bg-blue-50' : ''
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(client._id)}
                    onChange={() => {
                      setSelectedIds((prev) =>
                        prev.includes(client._id)
                          ? prev.filter((id) => id !== client._id)
                          : [...prev, client._id]
                      );
                      setSelectAll(false);
                    }}
                    className="rounded"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{client.companyName}</p>
                    <p className="text-xs text-gray-500">{client.phone} {client.contactName ? `- ${client.contactName}` : ''}</p>
                  </div>
                </label>
              ))}
              {filteredClients.length === 0 && (
                <p className="text-center text-gray-400 py-8 text-sm">Aucun client avec téléphone trouvé</p>
              )}
            </div>
          </div>

          {/* Composer */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 space-y-4">
            <h3 className="font-semibold text-gray-800">Composer le message</h3>

            {/* Canal */}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setChannel('whatsapp')}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                  channel === 'whatsapp'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                WhatsApp (gratuit)
              </button>
              <button
                type="button"
                onClick={() => setChannel('sms')}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                  channel === 'sms'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                SMS (payant)
              </button>
            </div>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Votre message SMS..."
              rows={6}
              maxLength={480}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
            />
            <div className="text-right text-xs text-gray-400">{content.length}/480 caractères</div>

            {selectedIds.length === 1 && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">Aperçu destinataire :</p>
                <p className="text-sm font-medium">
                  {clients.find((c) => c._id === selectedIds[0])?.companyName}
                </p>
                <p className="text-xs text-gray-500">
                  {clients.find((c) => c._id === selectedIds[0])?.phone}
                </p>
              </div>
            )}

            <button
              onClick={handleSend}
              disabled={sending || !content.trim() || !selectedIds.length}
              className={`w-full py-3 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                channel === 'whatsapp'
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {sending
                ? 'Envoi en cours...'
                : `Envoyer ${channel === 'whatsapp' ? 'via WhatsApp' : 'par SMS'} à ${selectedIds.length} client${selectedIds.length > 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      )}

      {tab === 'history' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-100 flex items-center gap-4">
            <select
              value={filterClientId}
              onChange={(e) => { setFilterClientId(e.target.value); setHistoryPage(1); }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">Tous les clients</option>
              {clients.map((c) => (
                <option key={c._id} value={c._id}>{c.companyName}</option>
              ))}
            </select>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Date</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Client</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Téléphone</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Message</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Statut</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {history.map((log) => (
                  <tr key={log._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                      {new Date(log.sentAt).toLocaleDateString('fr-FR', {
                        day: '2-digit', month: '2-digit', year: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                      })}
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-800">
                      {log.clientId?.companyName || '-'}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{log.phone}</td>
                    <td className="px-4 py-3 text-gray-600 max-w-xs truncate">{log.content}</td>
                    <td className="px-4 py-3">{getStatusBadge(log.status)}</td>
                  </tr>
                ))}
                {history.length === 0 && (
                  <tr><td colSpan={5} className="text-center py-8 text-gray-400">Aucun SMS envoyé</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {historyTotal > 20 && (
            <div className="p-4 flex items-center justify-between border-t border-gray-100">
              <button
                onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                disabled={historyPage === 1}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-50"
              >
                Précédent
              </button>
              <span className="text-sm text-gray-600">Page {historyPage}</span>
              <button
                onClick={() => setHistoryPage((p) => p + 1)}
                disabled={historyPage * 20 >= historyTotal}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-50"
              >
                Suivant
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SmsCampaign;
